<template>
    <Dialog :open="open" @update:open="$emit('close')">
        <DialogContent class="sm:max-w-lg">
            <DialogHeader>
                <DialogTitle>
                    <span v-if="isResuming">Resume your subscription</span>
                    <span v-else-if="isCanceling">Cancel your subscription</span>
                    <span v-else>Change your subscription</span>
                </DialogTitle>
            </DialogHeader>

            <div class="space-y-4">
                <div>
                    <Label class="text-sm font-medium text-foreground">Select plan</Label>
                    <Select v-model="selectedPlanUuid" class="mt-2">
                        <SelectTrigger>
                            <SelectValue placeholder="Choose a plan" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem
                                v-for="option in planOptions"
                                :key="option.value"
                                :value="option.value"
                            >
                                {{ option.label }}
                            </SelectItem>
                        </SelectContent>
                    </Select>
                </div>

                <!-- Pricing info for plan switch (skeleton while preparing to keep the dialog height stable) -->
                <div v-if="!isCanceling && !isResuming && (preparing || preparedSub)" class="rounded-md bg-new-neutral-50 p-4 space-y-2 text-sm">
                    <template v-if="preparing">
                        <div class="flex justify-between items-center">
                            <Skeleton class="h-4 w-20" />
                            <Skeleton class="h-4 w-28" />
                        </div>
                        <div class="flex justify-between items-center">
                            <Skeleton class="h-4 w-12" />
                            <Skeleton class="h-4 w-24" />
                        </div>
                        <div v-if="pluginSubscription.next_billing_date" class="flex justify-between items-center">
                            <Skeleton class="h-4 w-24" />
                            <Skeleton class="h-4 w-32" />
                        </div>
                    </template>
                    <template v-else>
                        <div class="flex justify-between">
                            <span class="text-muted-foreground">New plan</span>
                            <span class="font-medium text-foreground">{{ preparedSub.subscription_plan?.name }}</span>
                        </div>
                        <div class="flex justify-between">
                            <span class="text-muted-foreground">Price</span>
                            <span class="font-medium text-foreground">${{ (preparedSub.total / 100).toFixed(2) }}/{{ preparedSub.subscription_plan?.period === 12 ? 'year' : 'month' }}</span>
                        </div>
                        <div v-if="pluginSubscription.next_billing_date" class="flex justify-between">
                            <span class="text-muted-foreground">Next charge</span>
                            <span class="font-medium text-foreground">
                                {{ format(new Date(pluginSubscription.next_billing_date), 'd MMMM, yyyy') }}
                            </span>
                        </div>
                        <p class="text-xs text-muted-foreground leading-4">
                            The new plan is active immediately. We'll prorate the rest of the current period and bill you on the next charge date.
                        </p>
                    </template>
                </div>

                <!-- Resume context -->
                <div v-if="isResuming" class="flex items-start gap-3 rounded-md bg-new-blue-50 p-3">
                    <TriangleAlert class="mt-0.5 size-5 shrink-0 text-new-blue-600" />
                    <p class="text-sm text-new-blue-700">
                        Your subscription is currently scheduled to end. Resuming will keep it active.
                    </p>
                </div>

                <!-- Cancel context -->
                <div v-if="isCanceling" class="flex items-start gap-3 rounded-md bg-new-amber-100 p-3">
                    <TriangleAlert class="mt-0.5 size-5 shrink-0 text-new-amber-600" />
                    <p class="text-sm text-new-amber-800">
                        <span v-if="pluginSubscription.next_billing_date && new Date(pluginSubscription.next_billing_date) > new Date()">
                            Your current subscription will continue until {{ format(new Date(pluginSubscription.next_billing_date), 'd MMMM, yyyy') }}. After this date your subscription will be canceled.
                        </span>
                        <span v-else>
                            Your subscription will be canceled. You will no longer be able to use TOPOL Plugin.
                        </span>
                        Note that after your subscription is canceled, you will lose access to the images saved in our storage.
                    </p>
                </div>

                <!-- Same-as-current notice -->
                <p v-if="isSameAsCurrent" class="text-sm text-muted-foreground">
                    This is your current plan. Pick a different plan to switch.
                </p>

                <!-- Confirmation -->
                <label v-else class="flex items-start gap-3 cursor-pointer">
                    <Checkbox v-model="confirmed" class="mt-0.5" />
                    <span class="text-sm text-muted-foreground">
                        <span v-if="isResuming">I confirm that I want to resume my subscription</span>
                        <span v-else-if="isCanceling">I confirm that I want to CANCEL my subscription</span>
                        <span v-else>I confirm that I want to change my subscription plan</span>
                    </span>
                </label>
            </div>

            <DialogFooter class="mt-4">
                <Button size="lg" variant="ghost" @click="$emit('close')">{{ isSameAsCurrent ? 'Close' : 'Cancel' }}</Button>
                <Button
v-if="!isSameAsCurrent"
                    size="lg"
                    :variant="isCanceling ? 'destructive' : 'default'"
                    :disabled="submitDisabled"
                    @click="submit"
                >
                    <span v-if="isResuming">Resume</span>
                    <span v-else-if="isCanceling">Cancel subscription</span>
                    <span v-else>Change plan</span>
                </Button>
            </DialogFooter>
        </DialogContent>
    </Dialog>
</template>

<script lang="ts" setup>
import { ref, computed, watch, PropType } from 'vue';
import axios from 'axios';
import { router } from '@inertiajs/vue3';
import { route } from 'ziggy-js';
import { toast } from 'vue-sonner';
import { format } from 'date-fns';
import { TriangleAlert } from 'lucide-vue-next';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import SubscriptionPlan from '@/types/Subscription/SubscriptionPlan';
import ISubscription from '@/types/Subscription/ISubscription';
import useFeatures from '@/composables/useFeatures';
import { useStripeClient } from '@/composables/useStripe';
import { useGtm } from '@/composables/useGtm';

const props = defineProps({
    open: {
        type: Boolean,
        required: true,
    },
    pluginSubscription: {
        type: Object as PropType<ISubscription>,
        required: true,
    },
    activeSubscriptionPlans: {
        type: Array as PropType<SubscriptionPlan[]>,
        required: true,
    },
    subscriptionPlans: {
        type: Array as PropType<SubscriptionPlan[]>,
        required: true,
    },
    initialMode: {
        type: String as PropType<'switch' | 'cancel'>,
        default: 'switch',
    },
    initialPlanUuid: {
        type: String,
        default: '',
    },
});

const emit = defineEmits<{
    close: [];
    canceled: [];
}>();

const { teamFeatureDisabled } = useFeatures();
const { pushEvent } = useGtm();
const isStripeEnabled = !teamFeatureDisabled('stripe');
const { finalizeSubscriptionSwitch } = useStripeClient('plugin');

const CANCEL_VALUE = '__cancel__';
const currentPlanUuid = computed(() => props.pluginSubscription.subscription_plan_uuid);
const selectedPlanUuid = ref(currentPlanUuid.value);
const confirmed = ref(false);
const loading = ref(false);
const preparing = ref(false);
const preparedSub = ref<any>(null);

const isCanceling = computed(() => selectedPlanUuid.value === CANCEL_VALUE);
const isResuming = computed(
    () =>
        selectedPlanUuid.value === currentPlanUuid.value &&
        !!props.pluginSubscription.canceled_at
);
const isSameAsCurrent = computed(
    () =>
        !isCanceling.value &&
        !isResuming.value &&
        selectedPlanUuid.value === currentPlanUuid.value,
);

const submitDisabled = computed(() => {
    if (!confirmed.value || loading.value) return true;
    if (isResuming.value) return false;
    if (isCanceling.value) return false;
    if (selectedPlanUuid.value === currentPlanUuid.value) return true;
    return !preparedSub.value;
});

const planOptions = computed(() => {
    const sorted = [...props.activeSubscriptionPlans].sort((a, b) => a.price - b.price);

    const options = sorted.map((plan) => ({
        value: plan.uuid,
        label: `${plan.name} ($${plan.price_for_month / 100}/month)${plan.uuid === currentPlanUuid.value ? ' (current)' : ''}`,
    }));

    options.push({
        value: CANCEL_VALUE,
        label: 'Cancel Topol Plugin subscription',
    });

    return options;
});

watch(selectedPlanUuid, async (uuid) => {
    confirmed.value = false;
    preparedSub.value = null;

    if (uuid && uuid !== CANCEL_VALUE && uuid !== currentPlanUuid.value) {
        preparing.value = true;
        try {
            const res = await axios.get(
                route('subscriptions.prepare', {
                    subscription_plan_uuid: uuid,
                    additional_users: 0,
                })
            );
            preparedSub.value = res.data.data;
        } catch {
            // swallow — UI will show no pricing block
        } finally {
            preparing.value = false;
        }
    }
});

watch(() => props.open, (val) => {
    if (val) {
        if (props.initialMode === 'cancel') {
            selectedPlanUuid.value = CANCEL_VALUE;
        } else if (props.initialPlanUuid && props.initialPlanUuid !== currentPlanUuid.value) {
            selectedPlanUuid.value = props.initialPlanUuid;
        } else {
            selectedPlanUuid.value = currentPlanUuid.value;
        }
        confirmed.value = false;
        preparedSub.value = null;
    }
});

const submit = () => {
    if (submitDisabled.value) return;
    loading.value = true;

    if (isResuming.value) {
        resumeSubscription();
    } else if (isCanceling.value) {
        cancelSubscription();
    } else {
        switchPlan();
    }
};

const cancelSubscription = () => {
    const cancelRoute = isStripeEnabled
        ? route('stripe.subscriptions.cancel', { subscription: props.pluginSubscription.id })
        : route('subscriptions.cancel', { subscription: props.pluginSubscription.id });

    router.post(cancelRoute, {}, {
        preserveScroll: true,
        onSuccess: () => {
            toast.success('Subscription canceled');
            emit('canceled');
            emit('close');
        },
        onError: (errors) => {
            const message = (Object.values(errors)[0] as string) || 'Failed to cancel subscription';
            toast.error(message);
        },
        onFinish: () => {
            loading.value = false;
        },
    });
};

const switchPlan = () => {
    const switchRoute = isStripeEnabled
        ? route('stripe.subscriptions.update', { subscription: props.pluginSubscription.id })
        : route('subscriptions.switch', { subscription: props.pluginSubscription.id });

    const method = isStripeEnabled ? 'put' : 'post';

    router[method](
        switchRoute,
        { subscription_plan_uuid: selectedPlanUuid.value },
        {
            // The switch is not done when the request comes back: Stripe still needs
            // the proration invoice paid, so the dialog has to stay mounted long
            // enough to confirm the card and finalize.
            preserveState: true,
            preserveScroll: true,
            onSuccess: async (response: any) => {
                if (!isStripeEnabled) {
                    toast.success('Subscription plan changed');
                    loading.value = false;
                    emit('close');
                    return;
                }

                try {
                    const status = await finalizeSubscriptionSwitch(
                        props.pluginSubscription.id,
                        selectedPlanUuid.value,
                        response.props?.flash?.data,
                    );

                    toast.success(
                        status === 'pending'
                            ? 'Payment received — your new plan will be active shortly.'
                            : 'Subscription plan changed',
                    );
                } catch (error: any) {
                    toast.error(error?.message ?? 'Failed to change plan');
                    loading.value = false;
                    return;
                }

                loading.value = false;
                emit('close');
                router.reload({ preserveScroll: true });
            },
            onError: (errors) => {
                const message = (Object.values(errors)[0] as string) || 'Failed to change plan';
                toast.error(message);
                loading.value = false;
            },
        }
    );
};

const resumeSubscription = () => {
    // Stripe's subscriptions.resume API only works for *paused* subscriptions,
    // not canceled ones. Once a subscription is canceled in Stripe (or scheduled
    // to cancel at period end and has elapsed), the only way back is to start a
    // new subscription via the order flow. Match v1 behavior here.
    if (isStripeEnabled) {
        toast.error('Your subscription has been canceled. To restart, please subscribe to a plan again from the Subscription page.');
        loading.value = false;
        return;
    }

    router.post(
        route('subscriptions.resume', { subscription: props.pluginSubscription.id }),
        {},
        {
            preserveScroll: true,
            onSuccess: () => {
                toast.success('Subscription resumed');
                pushEvent('topol_purchase_renewal', {
                    type: 'plugin',
                    amountPaid: (preparedSub.value?.total ?? 0) / 100,
                    planType: preparedSub.value?.subscription_plan?.period === 1 ? 'monthly' : 'yearly',
                    currency: 'USD',
                });
                emit('close');
            },
            onError: (errors) => {
                const message = (Object.values(errors)[0] as string) || 'Failed to resume subscription';
                toast.error(message);
            },
            onFinish: () => {
                loading.value = false;
            },
        }
    );
};
</script>
